import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { dashboardApi, documentsApi, getErrorMessage } from '../services/api';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle2, 
  Eye,
  FileCheck,
  RefreshCw,
  AlertCircle,
  ChevronRight,
  Calendar,
  Users,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Progress bar component with label
const ProgressBar = ({ label, value, total, color, icon: Icon }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${color}`} />}
          <span className="text-sm font-medium text-foreground">{label}</span>
        </div>
        <span className="text-sm text-muted-foreground">{value} / {total} ({percentage}%)</span>
      </div>
      <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${color.replace('text-', 'bg-')}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Stat card with trend indicator
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color }) => (
  <Card className="neumorphic">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground mb-1">{title}</p>
          <p className="text-3xl font-bold font-heading">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
          <span>{Math.abs(trend)}% vs last period</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Simple bar chart visualization
const SimpleBarChart = ({ data, maxBars = 14 }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center text-muted-foreground">
        No data available
      </div>
    );
  }
  
  const displayData = data.slice(-maxBars);
  const maxValue = Math.max(...displayData.map(d => d.count), 1);
  
  return (
    <div className="h-40 flex items-end gap-1">
      {displayData.map((item, index) => (
        <div key={item.date} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-primary/80 rounded-t transition-all duration-300 hover:bg-primary"
            style={{ height: `${(item.count / maxValue) * 100}%`, minHeight: item.count > 0 ? '4px' : '0' }}
            title={`${item.date}: ${item.count} documents`}
          />
          {index % 2 === 0 && (
            <span className="text-[10px] text-muted-foreground rotate-0">
              {item.date.slice(5)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

export default function AnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [approvingId, setApprovingId] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await dashboardApi.getAnalytics(parseInt(period));
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (docId) => {
    setApprovingId(docId);
    try {
      await documentsApi.approve(docId);
      toast.success('Document approved successfully');
      loadAnalytics(); // Refresh data
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve document'));
    } finally {
      setApprovingId(null);
    }
  };

  if (loading && !analytics) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const { summary, progress, status_breakdown, documents_over_time, top_templates, pending_approval_list } = analytics || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Track document performance and completion rates</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]" data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadAnalytics} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Total Documents" 
            value={summary?.total_documents || 0}
            icon={FileText}
            color="bg-slate-700"
          />
          <StatCard 
            title="Open Rate" 
            value={`${summary?.open_rate || 0}%`}
            subtitle="Documents opened by recipients"
            icon={Eye}
            color="bg-blue-500"
          />
          <StatCard 
            title="Completion Rate" 
            value={`${summary?.completion_rate || 0}%`}
            subtitle="Documents fully completed"
            icon={CheckCircle2}
            color="bg-green-500"
          />
          <StatCard 
            title="Avg. Completion Time" 
            value={`${summary?.avg_completion_hours || 0}h`}
            subtitle="Average time to complete"
            icon={Clock}
            color="bg-amber-500"
          />
        </div>

        {/* Progress Bars Section */}
        <Card className="neumorphic">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Document Progress
            </CardTitle>
            <CardDescription>Overall document status distribution</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ProgressBar 
              label="Opened" 
              value={progress?.opened || 0} 
              total={progress?.total || 0}
              color="text-blue-500"
              icon={Eye}
            />
            <ProgressBar 
              label="Completed" 
              value={progress?.completed || 0} 
              total={progress?.total || 0}
              color="text-green-500"
              icon={CheckCircle2}
            />
            <ProgressBar 
              label="Pending Admin Approval" 
              value={progress?.pending_approval || 0} 
              total={progress?.total || 0}
              color="text-amber-500"
              icon={FileCheck}
            />
          </CardContent>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Documents Over Time */}
          <Card className="neumorphic">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Documents Over Time
              </CardTitle>
              <CardDescription>Documents sent in the last {period} days</CardDescription>
            </CardHeader>
            <CardContent>
              <SimpleBarChart data={documents_over_time} />
            </CardContent>
          </Card>

          {/* Top Templates */}
          <Card className="neumorphic">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Top Templates
              </CardTitle>
              <CardDescription>Most used document templates</CardDescription>
            </CardHeader>
            <CardContent>
              {top_templates && top_templates.length > 0 ? (
                <div className="space-y-3">
                  {top_templates.map((template, index) => (
                    <div key={template.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                          {index + 1}
                        </span>
                        <span className="font-medium truncate max-w-[200px]">{template.name}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{template.count} sent</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No template data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals */}
        <Card className="neumorphic border-amber-200">
          <CardHeader className="bg-amber-50/50 rounded-t-xl">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Pending Admin Approval
              {pending_approval_list?.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                  {pending_approval_list.length}
                </span>
              )}
            </CardTitle>
            <CardDescription>Documents completed by recipients awaiting your review</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {pending_approval_list && pending_approval_list.length > 0 ? (
              <div className="space-y-2">
                {pending_approval_list.map((doc) => (
                  <div 
                    key={doc.id} 
                    className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50/30 hover:bg-amber-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.candidate_name}</p>
                        <p className="text-sm text-muted-foreground">{doc.template_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        {doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, h:mm a') : 'N/A'}
                      </span>
                      <Button 
                        size="sm" 
                        onClick={() => handleApprove(doc.id)}
                        disabled={approvingId === doc.id}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-${doc.id}`}
                      >
                        {approvingId === doc.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={`/tracking`}>
                          <ChevronRight className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-50" />
                <p className="font-medium">All caught up!</p>
                <p className="text-sm">No documents pending approval</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Breakdown */}
        <Card className="neumorphic">
          <CardHeader>
            <CardTitle>Status Breakdown</CardTitle>
            <CardDescription>Detailed view of all document statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {[
                { label: 'Sent', value: status_breakdown?.sent || 0, color: 'bg-blue-100 text-blue-700' },
                { label: 'Opened', value: status_breakdown?.opened || 0, color: 'bg-cyan-100 text-cyan-700' },
                { label: 'In Progress', value: status_breakdown?.in_progress || 0, color: 'bg-purple-100 text-purple-700' },
                { label: 'Completed', value: status_breakdown?.completed || 0, color: 'bg-green-100 text-green-700' },
                { label: 'Pending Approval', value: status_breakdown?.pending_approval || 0, color: 'bg-amber-100 text-amber-700' },
                { label: 'Approved', value: status_breakdown?.approved || 0, color: 'bg-emerald-100 text-emerald-700' },
                { label: 'Expired', value: status_breakdown?.expired || 0, color: 'bg-red-100 text-red-700' },
              ].map((status) => (
                <div key={status.label} className={`p-4 rounded-xl ${status.color} text-center`}>
                  <p className="text-2xl font-bold">{status.value}</p>
                  <p className="text-xs font-medium">{status.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
